import React, { createContext, useContext, useEffect, useState } from 'react';
import { localBankIdDebugApi, type LocalBankIdDebugActor, type LocalBankIdDebugCapabilities } from '../services/api';
import { useAuth } from './AuthContext';

type DebugContext = {
  enabled: boolean;
  loading: boolean;
  unavailable: boolean;
  actors: LocalBankIdDebugActor[];
  environment?: LocalBankIdDebugCapabilities['environment'];
  allowTic: boolean;
  retry: () => void;
};
const initialContext = { enabled: false, loading: false, unavailable: false, actors: [], allowTic: false };
const LocalBankIdDebugContext = createContext<DebugContext>({ ...initialContext, retry: () => {} });

export function LocalBankIdDebugProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [context, setContext] = useState<Omit<DebugContext, 'retry'>>({ ...initialContext, loading: true });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const loadActors = () => { void localBankIdDebugApi.actors().then(result => {
      if (!result || typeof result.enabled !== 'boolean' || !Array.isArray(result.actors)) throw new Error('Invalid authentication capabilities');
      if (active) setContext({
        loading: false,
        unavailable: false,
        enabled: result.enabled,
        actors: result.actors,
        environment: result.environment,
        allowTic: result.allowTic === true && result.environment !== 'testserver'
      });
    }).catch(() => { if (active) setContext(previous => ({ ...previous, loading: false, unavailable: true, actors: [] })); }); };
    loadActors();
    const refreshVisibleActors = () => { if (document.visibilityState === 'visible') loadActors(); };
    window.addEventListener('focus', loadActors);
    document.addEventListener('visibilitychange', refreshVisibleActors);
    return () => {
      active = false;
      window.removeEventListener('focus', loadActors);
      document.removeEventListener('visibilitychange', refreshVisibleActors);
    };
  }, [user?.id, attempt]);
  const retry = () => { setContext(previous => ({ ...previous, loading: true })); setAttempt(value => value + 1); };
  return <LocalBankIdDebugContext.Provider value={{ ...context, retry }}>{children}</LocalBankIdDebugContext.Provider>;
}

export const useLocalBankIdDebug = () => useContext(LocalBankIdDebugContext);
